import { useQuery } from 'react-query';
import { WITHDRAW_BREAKDOWN } from '../appConstants/APIEndpoint';
import { requestApiPost } from './index';
import useDebouncedValue from '../hooks/useDebouncedValue';
import type {
  IWithdrawBreakdownData,
  IWithdrawBreakdownRequest,
  IWithdrawBreakdownResponse,
} from '../interfaces/Withdrawal';

/**
 * Recalculates the payout breakdown as the user types — step 2 of the flow.
 * Mirrors `WithdrawalViewModel.updatePayoutBreakdownData`, which debounces at
 * 500ms before hitting the API.
 *
 * The response also carries `confirmData`, the server-built confirm sheet.
 */
export const fetchWithdrawBreakdown = async (
  body: IWithdrawBreakdownRequest,
): Promise<IWithdrawBreakdownResponse> => {
  const res = await requestApiPost<IWithdrawBreakdownResponse>(
    WITHDRAW_BREAKDOWN,
    body,
  );
  const response = res?.response;

  if (!response?.success) {
    throw new Error(response?.error || 'Could not update the breakdown');
  }
  return response?.data ?? {};
};

interface IUseWithdrawBreakdown {
  amount?: number;
  sessionId?: string;
  /** Only PAYOUT is rendered in phase 1; the flags stay to keep the
   *  request shape identical to Android's. */
  isWithdrawAvailable?: boolean;
  isCTDAvailable?: boolean;
  isComboAvailable?: boolean;
  localeSelected?: string | null;
  enabled?: boolean;
}

export const useWithdrawBreakdown = ({
  amount,
  sessionId,
  isWithdrawAvailable = true,
  isCTDAvailable = false,
  isComboAvailable = false,
  localeSelected = null,
  enabled = true,
}: IUseWithdrawBreakdown) => {
  const debouncedAmount = useDebouncedValue(amount);

  const hasValidInput = Boolean(sessionId) && Number(debouncedAmount) > 0;

  const { data, isLoading, isFetching, error, refetch } = useQuery({
    // Keyed on the debounced amount, so react-query discards the response of a
    // superseded amount instead of letting it overwrite a fresher total.
    queryKey: ['withdrawBreakdown', sessionId, debouncedAmount, localeSelected],
    queryFn: () =>
      fetchWithdrawBreakdown({
        isWithdrawAvailable,
        isCTDAvailable,
        isComboAvailable,
        amount: Number(debouncedAmount),
        sessionId: sessionId as string,
        localeSelected,
      }),
    enabled: enabled && hasValidInput,
    retry: false,
    keepPreviousData: true,
    refetchOnWindowFocus: false,
  });

  const withdrawBreakdown: IWithdrawBreakdownData | undefined = data?.withdrawBreakdownData;

  return {
    withdrawBreakdown,
    breakdownResponse: data,
    /** True while the total on screen may not belong to the typed amount. */
    isStale: isFetching || debouncedAmount !== amount,
    isLoading,
    isFetching,
    error,
    refetch,
  };
};

export default useWithdrawBreakdown;
